import { useState, useEffect } from "react";
import SkeletonCard from "../common/SkeletonCard";
import FarmCard from "./FarmCard";
import { useFarmImagePreloader } from "../../hooks/useImagePreloader";

/**
 * FarmCardList component renders a list of FarmCard components.
 * It displays a loading skeleton when data is being fetched.
 * @param {Object} props 
 * @param {Array} props.farms - Array of farm objects to display
 * @param {string} props.style - CSS class for styling the list
 * @param {boolean} props.loading - Loading state to show skeletons
 * @param {Function} props.onFarmClick - Callback function when a farm card is clicked
 * @returns {JSX.Element} The rendered component
 */
function FarmCardList({ farms, style, loading, onFarmClick, onFarmHover, onFarmUnhover  }) {
  const [visibleFarms, setVisibleFarms] = useState(new Set());
  
  const FARM_REVEAL_DELAY_MS = 25; // Time between each farm card reveal
  const DEFAULT_FARM_ARRAY_LENGTH = 12; // Default number of skeletons to show when farms are empty

  // Use custom hook for image preloading
  const preloadComplete = useFarmImagePreloader(farms, loading, 3, 2000);

  // When preloading is complete, start showing farms sequentially
  useEffect(() => {
    if (!loading && farms.length > 0 && preloadComplete) {
      // Reset visible farms
      setVisibleFarms(new Set());
      
      // Show farms one by one with delays
      farms.forEach((farm, index) => {
        setTimeout(() => {
          setVisibleFarms(prev => new Set([...prev, farm.id]));
        }, index * FARM_REVEAL_DELAY_MS);
      });
    } else if (loading || farms.length === 0) {
      // Reset visible farms when loading or no farms
      setVisibleFarms(new Set());
    }
  }, [loading, farms, preloadComplete]);

  return (
    <div className={style}>
      {loading ? (
        // Show skeletons immediately
        Array.from({ length: farms.length || DEFAULT_FARM_ARRAY_LENGTH }).map((_, index) => (
          <SkeletonCard key={`skeleton-${index}`} />
        ))
      ) : (
        farms.map(farm => (
          visibleFarms.has(farm.id) ? (
            <div
              key={farm.id}
              onMouseEnter={() => onFarmHover(farm.id)}
              onMouseLeave={onFarmUnhover}
            >
              <FarmCard
                key={farm.id}
                farm={farm}
                onClick={() => onFarmClick(farm)}
                variant="list" // FarmList에서는 list 스타일 적용
              />
            </div>
          ) : (
            <SkeletonCard key={`waiting-${farm.id}`} />
          )
        ))
      )}
    </div>
  );
}

export default FarmCardList;